// Admin Fêtes 2027 — Detailed schedule editor (day / hour / pricing)
// CRUD entries for /api/admin/presence/schedule
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import PilotageDrawer from "./PilotageDrawer";

const CATEGORY_LABELS = {
  general: { fr: "Général", en: "General", color: "var(--ink-2)" },
  liturgie: { fr: "Liturgie", en: "Liturgy", color: "var(--lapis)" },
  atelier: { fr: "Atelier", en: "Workshop", color: "var(--cinnabar)" },
  conference: { fr: "Conférence", en: "Conference", color: "var(--gold)" },
  musique: { fr: "Musique", en: "Music", color: "var(--moss)" },
  repas: { fr: "Repas", en: "Meal", color: "var(--gold)" },
  autre: { fr: "Autre", en: "Other", color: "var(--muted)" },
};

const STATUS_LABELS = {
  en_creation: { fr: "En création", en: "Draft", bg: "rgba(117,120,130,0.14)", fg: "var(--muted)" },
  valide_sous_reserves: { fr: "Validé sous réserves", en: "Conditional", bg: "rgba(212,175,55,0.18)", fg: "var(--gold)" },
  valide: { fr: "Validé", en: "Validated", bg: "rgba(83,128,72,0.16)", fg: "var(--moss)" },
  annule: { fr: "Annulé", en: "Cancelled", bg: "rgba(153,27,27,0.14)", fg: "var(--cinnabar)" },
};
const KIND_LABELS = {
  standard: { fr: "Standard", en: "Standard", icon: "◉" },
  transport: { fr: "Transport", en: "Transport", icon: "→" },
  hebergement: { fr: "Hébergement", en: "Accommodation", icon: "⌂" },
};
const STATUS_KEYS = ["en_creation", "valide_sous_reserves", "valide", "annule"];
const KIND_KEYS = ["standard", "transport", "hebergement"];

const emptyEntry = () => ({
  day: "", start_time: "", end_time: "",
  title: { fr: "", en: "" }, description: { fr: "", en: "" }, location: { fr: "", en: "" },
  category: "general", kind: "standard", status: "en_creation",
  price_eur: 0, max_participants: 0, capacity: 0,
  partner_ids: [], member_ids: [],
  published: true,
});

export default function PresenceScheduleTab({ locale }) {
  const isFr = locale === "fr";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [pilotageId, setPilotageId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/presence/schedule");
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (e) => {
    if (!window.confirm(isFr ? `Supprimer « ${e.title?.fr || e.title?.en} » ?` : `Delete "${e.title?.fr || e.title?.en}"?`)) return;
    await api.delete(`/admin/presence/schedule/${e.id}`);
    load();
  };

  const togglePublished = async (e) => {
    await api.patch(`/admin/presence/schedule/${e.id}`, { published: !e.published });
    load();
  };

  const grouped = useMemo(() => {
    const map = {};
    items.forEach((it) => {
      const k = it.day || "—";
      (map[k] = map[k] || []).push(it);
    });
    return Object.keys(map).sort().map((day) => ({ day, entries: map[day] }));
  }, [items]);

  return (
    <div data-testid="presence-schedule-tab" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-2xl">{isFr ? "Planning détaillé des Fêtes 2027" : "Detailed schedule of Fêtes 2027"}</h3>
          <p className="font-body text-sm" style={{ color: "var(--muted)" }}>
            {isFr
              ? "Créneaux par jour et par heure : liturgies, ateliers, conférences, repas — avec tarifs optionnels."
              : "Day-by-day, hour-by-hour entries with optional pricing."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a className="btn btn-outline btn-sm"
             data-testid="schedule-pdf-programme"
             href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/presence/programme.pdf?locale=${locale}`}
             target="_blank" rel="noreferrer">
            📄 {isFr ? "Programme PDF" : "Programme PDF"}
          </a>
          <a className="btn btn-outline btn-sm"
             data-testid="schedule-pdf-livret"
             href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/presence/livret.pdf?locale=${locale}`}
             target="_blank" rel="noreferrer">
            📖 {isFr ? "Livret PDF" : "Booklet PDF"}
          </a>
          <button className="btn btn-primary" data-testid="schedule-new"
                  onClick={() => setEditing(emptyEntry())}>
            + {isFr ? "Nouveau créneau" : "New entry"}
          </button>
        </div>
      </div>

      {editing && (
        <ScheduleEditor isFr={isFr} initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}

      {loading && <div className="font-display italic" style={{ color: "var(--muted)" }}>…</div>}

      {!loading && items.length === 0 && (
        <div className="font-body text-sm italic" style={{ color: "var(--muted)" }}>
          {isFr ? "Aucun créneau. Créez-en un pour démarrer le planning." : "No entry yet."}
        </div>
      )}

      {!loading && grouped.map(({ day, entries }) => (
        <div key={day} data-testid={`schedule-day-${day}`}>
          <div className="deco-rule mb-3">
            <span className="font-caps text-[0.78rem]">
              {day !== "—"
                ? new Date(day).toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" })
                : (isFr ? "Sans date" : "No date")}
              <span style={{ color: "var(--muted)" }}> · {entries.length}</span>
            </span>
          </div>
          <div className="space-y-2">
            {entries.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "") || (a.order || 0) - (b.order || 0)).map((e) => {
              const cat = CATEGORY_LABELS[e.category] || CATEGORY_LABELS.general;
              return (
                <div key={e.id} data-testid={`schedule-${e.id}`}
                     className="bg-white border p-4 flex items-start gap-4 flex-wrap"
                     style={{ borderColor: "var(--border)", borderLeftWidth: 4, borderLeftColor: cat.color, opacity: e.published ? 1 : 0.55 }}>
                  <div className="font-mono text-sm" style={{ color: "var(--cinnabar)", minWidth: 110 }}>
                    {e.start_time}{e.end_time ? `–${e.end_time}` : ""}
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      {(() => {
                        const st = STATUS_LABELS[e.status || "en_creation"];
                        return (
                          <span data-testid={`schedule-status-${e.id}`}
                                className="font-caps text-[0.55rem] px-1.5 py-0.5 border"
                                style={{ borderColor: st.fg, color: st.fg, background: st.bg, letterSpacing: "0.18em" }}>
                            {isFr ? st.fr : st.en}
                          </span>
                        );
                      })()}
                      {e.kind && e.kind !== "standard" && (
                        <span className="font-caps text-[0.55rem] px-1.5 py-0.5 border"
                              style={{ borderColor: "var(--lapis)", color: "var(--lapis)", letterSpacing: "0.18em" }}>
                          {KIND_LABELS[e.kind].icon} {isFr ? KIND_LABELS[e.kind].fr : KIND_LABELS[e.kind].en}
                        </span>
                      )}
                      <span className="font-caps text-[0.55rem] px-1.5 py-0.5 border"
                            style={{ borderColor: cat.color, color: cat.color, letterSpacing: "0.18em" }}>
                        {isFr ? cat.fr : cat.en}
                      </span>
                      {e.price_eur > 0 && (
                        <span className="font-caps text-[0.55rem] px-1.5 py-0.5 border"
                              style={{ borderColor: "var(--cinnabar)", color: "var(--cinnabar)", letterSpacing: "0.15em" }}>
                          {e.price_eur.toFixed(2)} €
                        </span>
                      )}
                      {((e.max_participants ?? e.capacity) > 0) && (
                        <span className="font-caps text-[0.55rem]" style={{ color: "var(--muted)", letterSpacing: "0.15em" }}>
                          · MAX {e.max_participants || e.capacity} {isFr ? "PARTICIPANTS" : "ATTENDEES"}
                        </span>
                      )}
                      {!e.published && (
                        <span className="font-caps text-[0.55rem]" style={{ color: "var(--muted)", letterSpacing: "0.15em" }}>
                          · {isFr ? "BROUILLON" : "DRAFT"}
                        </span>
                      )}
                    </div>
                    <div className="font-display text-lg">{e.title?.fr || e.title?.en}</div>
                    {(e.description?.fr || e.description?.en) && (
                      <p className="font-body text-sm" style={{ color: "var(--ink-2)" }}>{e.description?.fr || e.description?.en}</p>
                    )}
                    {(e.location?.fr || e.location?.en) && (
                      <div className="font-body text-xs mt-1" style={{ color: "var(--muted)" }}>
                        📍 {e.location?.fr || e.location?.en}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button className="btn btn-outline btn-xs"
                            style={{ color: "var(--gold)" }}
                            data-testid={`schedule-pilotage-${e.id}`}
                            onClick={() => setPilotageId(e.id)}>
                      ◉ {isFr ? "Pilotage" : "Pilotage"}
                    </button>
                    <button className="btn btn-outline btn-xs" data-testid={`schedule-toggle-${e.id}`}
                            onClick={() => togglePublished(e)}>
                      {e.published ? (isFr ? "Masquer" : "Hide") : (isFr ? "Publier" : "Publish")}
                    </button>
                    <button className="btn btn-outline btn-xs" data-testid={`schedule-edit-${e.id}`}
                            onClick={() => setEditing({ ...e })}>
                      {isFr ? "Modifier" : "Edit"}
                    </button>
                    <button className="btn btn-outline btn-xs" style={{ color: "var(--cinnabar)" }}
                            data-testid={`schedule-delete-${e.id}`}
                            onClick={() => remove(e)}>
                      ✕
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {pilotageId && (
        <PilotageDrawer scheduleId={pilotageId} locale={locale}
                         onClose={() => { setPilotageId(null); load(); }} />
      )}
    </div>
  );
}

function ScheduleEditor({ initial, isFr, onCancel, onSaved }) {
  const [e, setE] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (path, val) => setE((s) => {
    const next = { ...s };
    if (Array.isArray(path)) next[path[0]] = { ...next[path[0]], [path[1]]: val };
    else next[path] = val;
    return next;
  });
  const save = async () => {
    setErr("");
    if (!e.day) { setErr(isFr ? "Jour requis." : "Day required."); return; }
    if (!e.start_time) { setErr(isFr ? "Heure de début requise." : "Start time required."); return; }
    if (!e.title.fr && !e.title.en) { setErr(isFr ? "Titre requis (FR ou EN)." : "Title required."); return; }
    setBusy(true);
    try {
      const payload = {
        day: e.day, start_time: e.start_time, end_time: e.end_time || "",
        title: e.title, description: e.description, location: e.location,
        category: e.category,
        kind: e.kind || "standard",
        status: e.status || "en_creation",
        price_eur: parseFloat(e.price_eur || 0),
        max_participants: parseInt(e.max_participants ?? e.capacity ?? 0, 10),
        capacity: parseInt(e.max_participants ?? e.capacity ?? 0, 10),
        partner_ids: e.partner_ids || [],
        member_ids: e.member_ids || [],
        published: e.published,
        order: parseInt(e.order || 0, 10),
      };
      if (e.id) await api.patch(`/admin/presence/schedule/${e.id}`, payload);
      else await api.post("/admin/presence/schedule", payload);
      onSaved();
    } catch (er) {
      setErr(er?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };
  return (
    <div className="admin-card space-y-4" data-testid="schedule-editor">
      <h3 className="font-display text-xl">
        {e.id ? (isFr ? "Modifier le créneau" : "Edit entry") : (isFr ? "Nouveau créneau" : "New entry")}
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <label className="block">
          <span className="label-caps">{isFr ? "Jour *" : "Day *"}</span>
          <input type="date" className="input-classic mt-1" value={e.day} onChange={(ev) => set("day", ev.target.value)} data-testid="schedule-day" />
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Heure début *" : "Start *"}</span>
          <input type="time" className="input-classic mt-1" value={e.start_time}
                 onChange={(ev) => set("start_time", ev.target.value)} data-testid="schedule-start" />
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Heure fin" : "End"}</span>
          <input type="time" className="input-classic mt-1" value={e.end_time || ""}
                 onChange={(ev) => set("end_time", ev.target.value)} data-testid="schedule-end" />
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Catégorie" : "Category"}</span>
          <select className="input-classic mt-1" value={e.category} onChange={(ev) => set("category", ev.target.value)} data-testid="schedule-cat">
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{isFr ? v.fr : v.en}</option>)}
          </select>
        </label>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="label-caps">{isFr ? "Titre FR *" : "Title FR *"}</span>
          <input className="input-classic mt-1" value={e.title.fr} onChange={(ev) => set(["title", "fr"], ev.target.value)} data-testid="schedule-title-fr" />
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Titre EN" : "Title EN"}</span>
          <input className="input-classic mt-1" value={e.title.en} onChange={(ev) => set(["title", "en"], ev.target.value)} data-testid="schedule-title-en" />
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Description FR" : "Description FR"}</span>
          <textarea rows={2} className="input-classic mt-1" value={e.description.fr} onChange={(ev) => set(["description", "fr"], ev.target.value)} />
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Description EN" : "Description EN"}</span>
          <textarea rows={2} className="input-classic mt-1" value={e.description.en} onChange={(ev) => set(["description", "en"], ev.target.value)} />
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Lieu FR" : "Location FR"}</span>
          <input className="input-classic mt-1" value={e.location.fr} onChange={(ev) => set(["location", "fr"], ev.target.value)} />
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Lieu EN" : "Location EN"}</span>
          <input className="input-classic mt-1" value={e.location.en} onChange={(ev) => set(["location", "en"], ev.target.value)} />
        </label>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <label className="block">
          <span className="label-caps">{isFr ? "Statut" : "Status"}</span>
          <select className="input-classic mt-1" value={e.status || "en_creation"}
                  onChange={(ev) => set("status", ev.target.value)} data-testid="schedule-status-select">
            {STATUS_KEYS.map((k) => <option key={k} value={k}>{isFr ? STATUS_LABELS[k].fr : STATUS_LABELS[k].en}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Type" : "Kind"}</span>
          <select className="input-classic mt-1" value={e.kind || "standard"}
                  onChange={(ev) => set("kind", ev.target.value)} data-testid="schedule-kind">
            {KIND_KEYS.map((k) => <option key={k} value={k}>{isFr ? KIND_LABELS[k].fr : KIND_LABELS[k].en}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Tarif (€)" : "Price (€)"}</span>
          <input type="number" step="0.5" className="input-classic mt-1" value={e.price_eur}
                 onChange={(ev) => set("price_eur", ev.target.value)} data-testid="schedule-price" />
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Max participants" : "Max attendees"}</span>
          <input type="number" className="input-classic mt-1" value={e.max_participants ?? e.capacity ?? 0}
                 onChange={(ev) => set("max_participants", ev.target.value)} data-testid="schedule-max" />
        </label>
        <label className="flex items-center gap-2 font-body">
          <input type="checkbox" checked={e.published} onChange={(ev) => set("published", ev.target.checked)} data-testid="schedule-published" />
          {isFr ? "Publié" : "Published"}
        </label>
      </div>
      {err && <p className="text-sm" style={{ color: "var(--cinnabar)" }}>{err}</p>}
      <div className="flex gap-3 justify-end">
        <button className="btn btn-outline" onClick={onCancel} data-testid="schedule-cancel">{isFr ? "Annuler" : "Cancel"}</button>
        <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="schedule-save">
          {busy ? "…" : (isFr ? "Enregistrer" : "Save")}
        </button>
      </div>
    </div>
  );
}
